import React, { useEffect } from "react"
import { useLocalizedStrings } from "../../../Localization"
import { CreateComponentProps } from "./CreateDashboardComponentTab"
import { useDashboards } from "../../../Contexts/DashboardsContext"
import { useReports } from "../../../Contexts/ReportsContext"
import { createStagedComponent } from "@aidkitorg/types/lib/admin-panel/dashboard"
import { Button, Icon } from "@aidkitorg/component-library"
import { NavLink } from "react-router-dom"

const CreateNewReportButton = ({ fullWidth = false }: { fullWidth?: boolean }) => {
  return (
    <NavLink
      to="/admin-panel/reports/new"
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 px-3 py-2 rounded text-sm font-bold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors duration-300 ${fullWidth ? 'w-full justify-center' : ''}`}
      style={{
        backgroundColor: 'var(--ak-color-button-secondary-background)',
        border: 'var(--ak-color-button-secondary-border)',
        color: 'var(--ak-color-button-secondary-text)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--ak-color-button-secondary-background-hover)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--ak-color-button-secondary-background)';
      }}
    >
      <Icon name="PlusIcon" type="solid" size="sm" />
      Create New Report
      <Icon name="ArrowTopRightOnSquareIcon" type="solid" size="sm" />
    </NavLink>
  )
}

export const ReportComponentConfig = ({ setDisplayMode }: CreateComponentProps) => {
  const L = useLocalizedStrings()
  const { reports, getReports, isReportsLoading } = useReports()
  const { setStagedComponents } = useDashboards()

  useEffect(() => {
    getReports()
  }, [])

  const handleSelectReport = async (reportId: string) => {
    const selectedReport = reports.find(r => r.id === reportId)
    if (!selectedReport) return

    const reportComponentConfig = {
      kind: 'Report' as const,
      title: 'My Report', // Default title, we shouldn't actually be using this as DashboardComponentCards will fetch the true report title
      reportId: reportId
    }

    // Report components bypass translation - add directly to staged components
    const stagedComponent = createStagedComponent(reportComponentConfig)
    setStagedComponents(prev => [...prev, stagedComponent])
    setDisplayMode('create')
  }

  if (isReportsLoading) {
    return <div className="min-h-64 items-center justify-center flex">
      <div className="text-gray-600">{L.admin_panel.reports.loading}...</div>
    </div>
  }

  if (reports.length === 0) {
    return (
      <div className="flex flex-col gap-y-4">
        <div className="text-xl font-bold text-gray-900">Add Report</div>
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="mb-4">
            <Icon name="DocumentTextIcon" type="outline" size="xl" className="text-gray-400" />
          </div>
          <div className="text-lg font-medium mb-2">
            {L.admin_panel.dashboards.no_reports_available}
          </div>
          <div className="text-sm text-gray-600 mb-6 max-w-sm">
            {L.admin_panel.dashboards.no_reports_available_msg}
          </div>
          <CreateNewReportButton />
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-y-2">
      <div className="text-xl font-bold text-gray-900">{L.admin_panel.dashboards.add_report}</div>
      <div className="text-sm text-gray-600 mb-2">{L.admin_panel.dashboards.select_report_msg}</div>
      
      <div className="flex flex-col gap-y-2 max-h-96 p-1 overflow-y-auto">
        {reports.map((report) => (
          <Button
            key={report.id}
            variant="secondary"
            className="!w-full !justify-start !min-h-16"
            onClick={() => handleSelectReport(report.id)}
          >
            <div className="flex items-center gap-x-3 w-full">
              <div>
                <Icon type="solid" name="TableCellsIcon" role="decorative"/>
              </div>
              <div className="text-left flex-1">
                <div className="font-medium truncate">
                  {report.title}
                </div>
                <div className="text-gray-600 font-normal text-sm truncate">
                  {report.description || 'No description'}
                </div>
                <div className="text-gray-500 font-normal text-xs">
                  {L.admin_panel.dashboards.updated} {new Date(report.updated_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          </Button>
        ))}
      </div>
      
      <div className="pt-2 border-t">
        <CreateNewReportButton fullWidth />
      </div>
    </div>
  )
}